import React, { ReactNode } from 'react';

// Design System Components
import EdgeCollapseToggle from './EdgeCollapseToggle';

// Hooks
import { EditorChromeState } from '../../hooks';

// Constants
import { STRINGS } from '../../constants';

interface EditorBodyProps {
  chrome: EditorChromeState;
  /** The configuration pane — always the first column in DOM order (spec R59). */
  pane: ReactNode;
  preview: ReactNode;
  /** Null while nothing is selected: then the editor has two columns (spec R65). */
  panel: ReactNode | null;
}

/**
 * The editor body's **column topology**, and nothing else (spec R59, R63, R65,
 * R75–R77).
 *
 * Three children in flow, left to right: a width-controlled wrapper holding the
 * configuration pane, the preview at `tw-flex-1 tw-min-w-0`, and a second
 * width-controlled wrapper holding the properties panel while one is open. Collapse
 * flips a side wrapper's width class to `tw-w-0` and nothing else, so the width it
 * releases goes to the preview by the flex algorithm itself rather than by any
 * arithmetic here (ADR-5B) — and because every column is in flow, no panel can ever
 * be a layer over the preview (R77, ADR-3A).
 *
 * A collapsed column's content is **unmounted**, not hidden (ADR-5D): a zero-width
 * box with focusable children in it is a dead zone a keyboard user can still tab
 * into. The panel's own values live on the card and its selection lives in
 * `useInspector`, so remounting restores both with no panel-local memory (R76).
 *
 * The two edge controls are absolutely positioned inside this row's `tw-relative`
 * box, above the columns, and are rendered **first** so they lead in tab order —
 * the pane's own control before the pane it opens. Each sits at its column's outer
 * edge while that column is open and at the body's edge while it is closed, which
 * is what keeps a collapsed panel reopenable (R75, R76).
 *
 * **The row is where the editor's height stops** (spec R74). From `md:` up it takes
 * the height `CardEditor` has left over and clips it, so its three children are
 * stretched to a real, bounded height and each one scrolls its own overflow. The
 * columns used to reach for `sticky` + `max-h-screen` instead, which assumed the
 * *document* was the scrolling ancestor and left a 100vh ceiling no realistic column
 * ever reached: a wheel over any of them found no scrollable box, bubbled to the app
 * shell's `main`, and scrolled all three columns out of view together. Nothing here
 * is `sticky` any more — the row's own bound is what pins the columns.
 *
 * Below `md:` the columns stack (`tw-flex-wrap`) and the page scrolls them, so none
 * of the height posture applies there: an unbounded stack inside a clipped row would
 * simply cut the last column off.
 */

/** Both side columns are 320px while expanded (spec R59/Q30, R63). */
const EXPANDED_WIDTH_CLASS = 'tw-w-full md:tw-w-80';
/** …so each control sits 320px in from its own edge while its column is open. */
const PANE_TOGGLE_OFFSET_CLASS = 'md:tw-left-80';
const PANEL_TOGGLE_OFFSET_CLASS = 'md:tw-right-80';

/**
 * A side column's wrapper.
 *
 * `overflow-clip`, NOT `overflow-hidden`. They clip identically, and only one of
 * them is safe here: **`overflow: hidden` is a scroll container** — it merely
 * hides the scrollbar — so anything inside it can still be scrolled
 * programmatically, and the user has no scrollbar to scroll back with.
 *
 * That is not theoretical. Ticking a field's checkbox focuses it, and the
 * browser then scrolls it into view by walking up EVERY scrollable ancestor —
 * past `FieldList`'s own `overflow-y-auto`, into this wrapper and the row below.
 * Measured: the row ended at `scrollTop: 459` with the columns at `top: -338`,
 * i.e. the entire editor body pushed above its own clip. The screen went blank
 * with no way back short of a reload, and no error anywhere — it is a layout
 * trap, not a crash.
 *
 * `overflow: clip` is not a scroll container, so scroll-into-view cannot move
 * it. It keeps the flex-item `min-height: auto` -> 0 behaviour this layout also
 * relies on, since that applies to any `overflow` other than `visible`.
 */
const columnWrapperClass = (collapsed: boolean) =>
  `tw-shrink-0 tw-overflow-clip ${collapsed ? 'tw-w-0' : EXPANDED_WIDTH_CLASS}`;

const EditorBody = ({ chrome, pane, preview, panel }: EditorBodyProps) => (
  <div className="tw-relative tw-flex tw-flex-wrap md:tw-flex-nowrap tw-items-stretch tw-min-w-0 md:tw-flex-1 md:tw-min-h-0 md:tw-overflow-clip">
    <EdgeCollapseToggle
      id="product-cards-pane-toggle"
      side="left"
      collapsed={chrome.paneCollapsed}
      label={STRINGS.chrome.togglePane}
      expandedOffsetClass={PANE_TOGGLE_OFFSET_CLASS}
      onToggle={chrome.togglePane}
    />

    {/* The inspector is always mounted since 2026-08-15 — with nothing selected it
        holds the card's own properties — so its collapse control is always there
        too (spec R76, amended). It used to appear only while a field was selected,
        which meant the toggle blinked in and out and the preview jumped 320px on
        every selection. */}
    <EdgeCollapseToggle
      id="product-cards-panel-toggle"
      side="right"
      collapsed={chrome.panelCollapsed}
      label={STRINGS.chrome.togglePanel}
      expandedOffsetClass={PANEL_TOGGLE_OFFSET_CLASS}
      onToggle={chrome.togglePanel}
    />

    <div className={columnWrapperClass(chrome.paneCollapsed)}>
      {!chrome.paneCollapsed && pane}
    </div>

    {/* The preview absorbs whatever the side columns are not using, and — stretched
        to the row's own bounded height — scrolls its own overflow, so a wheel over it
        finds a scrollable box here and moves neither of the other two columns
        (spec R74, R77). Its horizontal padding leaves the edge controls a lane of
        their own. */}
    <div className="tw-flex-1 tw-min-w-0 tw-p-4 md:tw-px-8 md:tw-h-full md:tw-overflow-y-auto">
      {preview}
    </div>

    <div className={columnWrapperClass(chrome.panelCollapsed)}>
      {!chrome.panelCollapsed && panel}
    </div>
  </div>
);

export default EditorBody;
